import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  DynamicField,
  Checkbox,
  Label,
  ThreeLevelCategorySelector,
} from "@b2b/ui-components";
import { ExternalLink } from "lucide-react";

import { rfqFormConfig } from "../config/DetailedRfqForm.config";

interface CategoryHierarchy {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  level?: number;
  children?: CategoryHierarchy[];
  productCount?: number;
}

const DetailedRFQForm = ({ 
  switchToQuick,
  onSubmit,
  onSaveDraft,
  loading = false,
  categories = [],
  initialData,
  isEditMode = false,
}: { 
  switchToQuick: () => void;
  onSubmit?: (formData: Record<string, any>) => void;
  onSaveDraft?: (formData: Record<string, any>) => void;
  loading?: boolean;
  categories?: CategoryHierarchy[];
  initialData?: Record<string, any>;
  isEditMode?: boolean;
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(initialData || {});
  const [filesMap, setFilesMap] = useState<Record<string, File[]>>({});

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = () => {
    if (onSaveDraft) {
      onSaveDraft({ ...formData, files: filesMap });
    } else {
      console.log("Draft Saved", formData);
    }
  };

  const handleSubmitRFQ = () => {
    if (!formData.industry || !formData.leadName || !formData.description) {
      console.log("Required fields missing");
      return;
    }
    if (onSubmit) {
      onSubmit({ ...formData, files: filesMap });
    } else {
      console.log("RFQ Submitted", formData);
    }
  };

  return (
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
          {isEditMode ? "Edit RFQ" : "Detailed RFQ Creation"}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          {isEditMode 
            ? "Update your Request for Quote with new specifications and terms."
            : "Create a comprehensive Request for Quote with detailed specifications and terms."}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Detailed Form</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            type="multiple"
            defaultValue={["basic-info", "additional-details"]}
            className="space-y-4"
          >
            <AccordionItem
              value="basic-info"
              className="border rounded-lg px-4"
            >
              <AccordionTrigger className="text-lg font-semibold text-primary">
                Basic Information
              </AccordionTrigger>
              <AccordionContent className="pt-6 space-y-6">
                {/* Category Selection (Three-level hierarchy) */}
                {categories.length > 0 && (
                  <div className="col-span-full">
                    <ThreeLevelCategorySelector
                      categories={categories}
                      selectedCategoryIds={formData.categoryIds || []}
                      onChange={(categoryIds) => handleInputChange("categoryIds", categoryIds)}
                      label="Categories"
                      required={true}
                      allowMultiSelect={true}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rfqFormConfig.basicInfo.map((field) => (
                    <DynamicField
                      key={field.name}
                      field={field}
                      value={formData[field.name]}
                      onChange={(val) => handleInputChange(field.name, val)}
                      filesMap={filesMap}
                      setFilesMap={setFilesMap}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="additional-details"
              className="border rounded-lg px-4"
            >
              <AccordionTrigger className="text-lg font-semibold text-primary">
                Additional Details
              </AccordionTrigger>
              <AccordionContent className="pt-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rfqFormConfig.additionalDetails
                    .filter((field) => field.type !== "file")
                    .map((field) => (
                      <DynamicField
                        key={field.name}
                        field={field}
                        value={formData[field.name]}
                        onChange={(val) => handleInputChange(field.name, val)}
                        filesMap={filesMap}
                        setFilesMap={setFilesMap}
                      />
                    ))}
                </div>

                <div className="flex flex-wrap gap-6 mt-4">
                  {rfqFormConfig.additionalDetails
                    .filter((field) => field.type === "file")
                    .map((field) => (
                      <DynamicField
                        key={field.name}
                        field={field}
                        value={formData[field.name]}
                        onChange={(val) => handleInputChange(field.name, val)}
                        filesMap={filesMap}
                        setFilesMap={setFilesMap}
                      />
                    ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleSaveDraft} disabled={loading}>
            Save Draft
          </Button>
          <Button variant="primary" onClick={handleSubmitRFQ} disabled={loading}>
            {loading ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update RFQ" : "Submit RFQ")}
          </Button>
        </div>
        <Button
          variant="link"
          className="gap-2 text-primary"
          onClick={switchToQuick}
        >
          Switch to Quick Mode
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default DetailedRFQForm;
